import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useTargetBrandConfig } from 'FrontRoyalAngular';
import { MailtoLink } from 'MailtoLink';
import { type LearnerProject } from 'LearnerProjects';
import { Modal } from 'Modal';
import { twMerge } from 'tailwind-merge';
import { Text } from 'Text';
import { type UserAttrs } from 'Users/types/UserAttrs.types';
import { CreateProjectGroupModalPage } from './CreateProjectGroupModalPage';
import { getCreatorMemberDetails } from './getMemberDetails';
import './locales/project_group_setup_modal-en.json';
import { type MemberDetails, CreateProjectGroupModalPages } from './ProjectGroup.types';
import { ProjectGroupCreationSuccessModalPage } from './ProjectGroupCreationSuccessModalPage';
import { ConfirmProjectGroupModalPage } from './ConfirmProjectGroupModalPage';

const Footnote = ({ className }: { className?: string }) => {
    const { t } = useTranslation('back_royal');
    const brandConfig = useTargetBrandConfig();
    const projectEmailAddress = brandConfig.emailAddressForUsername('projects');
    return (
        <div className={twMerge('flex justify-center text-center', className)}>
            <Text as="small" color="slate-grey">
                {t('project_group_setup_modal.footnote', {
                    email: <MailtoLink className="text-xs" email={projectEmailAddress} key="projects-mailto" />,
                })}
            </Text>
        </div>
    );
};

function ProjectGroupSetupModalComponent(props: { closeModal: () => void; isOpen: boolean; project: LearnerProject }) {
    const { closeModal, isOpen, project } = props;
    const currentUser = useCurrentUser();
    const [currentModalPage, setCurrentModalPage] = useState<CreateProjectGroupModalPages>(
        CreateProjectGroupModalPages.MEMBER_SEARCH,
    );
    const { t } = useTranslation('back_royal');
    const title =
        currentModalPage === CreateProjectGroupModalPages.MEMBER_SEARCH
            ? t('project_group_setup_modal.member_search_page_title')
            : currentModalPage === CreateProjectGroupModalPages.REVIEW_AND_CONFIRM
              ? t('project_group_setup_modal.review_and_confirm_page_title')
              : currentModalPage === CreateProjectGroupModalPages.SUCCESS
                ? t('project_group_setup_modal.success_page_title')
                : null;

    const [members, setMembers] = useState<MemberDetails[]>([getCreatorMemberDetails(currentUser as UserAttrs)]);

    if (!isOpen) return null;

    return (
        <Modal
            classNames={{
                base: 'w-[650px] max-w-[90vw]',
                header: 'text-center',
                body: 'mx-7.5 mb-6',
            }}
            dismissOnEscape={false}
            dismissOnOverlayClick={false}
            onClose={closeModal}
            scrollBehavior="outside"
            title={title}
        >
            {currentModalPage === CreateProjectGroupModalPages.MEMBER_SEARCH && (
                <CreateProjectGroupModalPage
                    project={project}
                    members={members}
                    setMembers={setMembers}
                    onCancel={closeModal}
                    onConfirm={() => setCurrentModalPage(CreateProjectGroupModalPages.REVIEW_AND_CONFIRM)}
                />
            )}
            {currentModalPage === CreateProjectGroupModalPages.REVIEW_AND_CONFIRM && (
                <ConfirmProjectGroupModalPage
                    project={project}
                    members={members}
                    onCancel={() => setCurrentModalPage(CreateProjectGroupModalPages.MEMBER_SEARCH)}
                    onConfirm={() => setCurrentModalPage(CreateProjectGroupModalPages.SUCCESS)}
                />
            )}
            {currentModalPage === CreateProjectGroupModalPages.SUCCESS && (
                <ProjectGroupCreationSuccessModalPage project={project} onConfirm={closeModal} />
            )}
            <Footnote className="mt-3.5" />
        </Modal>
    );
}
export const ProjectGroupSetupModal = memo(ProjectGroupSetupModalComponent);
